import { createContext, useContext, type ReactNode } from 'react';

// A pure context lets page views and stories opt into the new frame without
// importing Firebase or a router. Only the app mount resolves the live flag.
export const HubFrameContext = createContext(false);
export const useHubFrame = () => useContext(HubFrameContext);

/**
 * THE-1161's full-width sticky header + edge-to-edge carousels used to gate
 * on a second, independent preview switch here (`hubWideGutterFlag.ts`) —
 * Kate, 2026-10-28 (PR #883): don't ship it behind its own on/off, ride the
 * existing Gift Guide hub preview link/gate only. `HubNavigationFrame.tsx`
 * now sets this context to the SAME value as `HubFrameContext` (both only
 * ever provided once `useGiftGuideHubGate()` is `'enabled'`) rather than
 * resolving a separate Firestore flag — kept as its own context, not merged
 * into `useHubFrame`, so `GuideBoardView`/`GuideProductCard`/
 * `RelatedGuidesRail` and the rest of this file's consumers need no change.
 */
export const HubWideGutterContext = createContext(false);
export const useHubWideGutterFrame = () => useContext(HubWideGutterContext);

// Collection pages place the existing frame navigation inside their banner —
// LikedPage's per-person detail route (THE-1187) reuses the same hand-off so
// its own accent wash can sit behind the desktop header too.
export const CollectionNavigationContext = createContext<ReactNode>(null);

// THE-1263: the `background` CSS BoardLayout wants the Hub frame's desktop
// top bar to paint while its big header card sits at rest — a DOM box the
// board layout never reaches, since the frame renders it as a sibling ahead
// of the board's whole layout rather than handing it down the way the
// collection/liked-detail hand-off above does (the board's wash isn't a
// fixed per-page color like theirs; it turns on and off as the header card
// collapses, so a setter the board calls on every change fits better here
// than a single ReactNode baked in at mount). `null` clears it (header
// collapsed, route not a board, or no hub frame at all), restoring the
// bar's plain white.
//
// NOT always a flat color (Kate 2026-10-27: the wash must run as one fade
// "straight through the hub top bar," not stop at a flat band under it) —
// BoardLayout measures its own wash wrapper's height and, once it has a real
// number, hands this bar a gradient that fades from the wash's start color to
// the exact shade its own wrapper picks up at, so the two paint one
// continuous fade rather than two independently-tinted boxes. Before that
// measurement lands (or outside a hub frame's own wash wrapper entirely) it's
// still the flat start color, which is why this stays `string | null`
// rather than a narrower "always a gradient" type.
export const HubBoardWashContext = createContext<(wash: string | null) => void>(() => {});
